import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Form, Button } from "react-bootstrap";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../styles/TeacherPage.css";
import Footer from "../FooterBn";

function TeacherPage() {
  const { teacherId } = useParams();
  const navigate = useNavigate();
  const rateRef = useRef(null);

  const [teacher, setTeacher] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [teacherAverageRatings, setTeacherAverageRatings] = useState(null);

  const [ratings, setRatings] = useState({
    proficiency: 3,
    connection: 3,
    clarity: 3,
    behavior: 3,
    supportiveness: 3,
  });

  const [anonymous, setAnonymous] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [teacherTitle, setTeacherTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const categories = useMemo(
    () => [
      {
        label: "Proficiency",
        key: "proficiency",
        avgKey: "avg_proficiency",
        icon: "🎓",
      },
      {
        label: "Connection with Students",
        key: "connection",
        avgKey: "avg_connection",
        icon: "🤝",
      },
      {
        label: "Clarity in Teaching",
        key: "clarity",
        avgKey: "avg_clarity",
        icon: "🗣️",
      },
      {
        label: "Behavior & Attitude",
        key: "behavior",
        avgKey: "avg_behavior",
        icon: "😊",
      },
      {
        label: "Supportiveness",
        key: "supportiveness",
        avgKey: "avg_supportiveness",
        icon: "💪",
      },
    ],
    [],
  );

  const calculateTeacherTitle = (avg) => {
    if (avg >= 4.6) return "G.O.A.T Teacher";
    if (avg >= 4.2) return "Top Class Teacher";
    if (avg >= 3.6) return "Perfect Teacher";
    if (avg >= 3.0) return "Almost Perfect Teacher";
    return "Needs Improvement";
  };

  const getToken = () => localStorage.getItem("token");
  const isLoggedIn = !!getToken();

  const getRatingColor = (rating) => {
    const r = Number(rating) || 0;
    if (r <= 1) return "rgb(255,140,90)";
    if (r <= 2) return "rgb(255,178,52)";
    if (r <= 3) return "rgb(255,217,52)";
    if (r <= 4) return "rgb(109, 162, 84)";
    return "rgb(46, 187, 91)";
  };

  const fetchTeacher = async () => {
    const res = await axios.get(
      `https://server.goatteachers.com/api/teachers/${teacherId}`,
    );
    setTeacher(res.data);
  };

  const fetchTeacherAverage = async () => {
    const res = await axios.get(
      `https://server.goatteachers.com/api/teachers/${teacherId}/average-rating`,
    );
    setTeacherAverageRatings(res?.data?.averageRatings || null);
  };

  const fetchReviews = async () => {
    const res = await axios.get(
      `https://server.goatteachers.com/api/teachers/${teacherId}/reviews`,
    );
    setReviews(res?.data?.reviews || []);
  };

  const computeTitleFromReviews = (list) => {
    if (!list?.length) return "";
    const avg =
      list.reduce((acc, review) => {
        return (
          acc +
          (review.proficiency_rating +
            review.connection_rating +
            review.clarity_rating +
            review.behavior_rating +
            review.supportiveness_rating) /
            5
        );
      }, 0) / list.length;

    return calculateTeacherTitle(avg);
  };

  useEffect(() => {
    if (isLoggedIn) return;

    const initialTimer = setTimeout(() => {
      toast.info("Please login or sign up to submit a rating.", {
        toastId: "teacher-login-required-initial",
        position: "top-right",
        autoClose: 3000,
      });
    }, 1200);

    const interval = setInterval(() => {
      toast.info("Login / Sign Up required to rate this teacher.", {
        toastId: `teacher-login-required-${Date.now()}`,
        position: "top-right",
        autoClose: 3000,
      });
    }, 15000);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, [isLoggedIn]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setError("");

        await Promise.all([
          fetchTeacher(),
          fetchTeacherAverage(),
          fetchReviews(),
        ]);

        // after reviews load, compute title
        // NOTE: we re-fetch reviews above, so use a separate fetch to get current state is tricky
        // easiest: call API again quickly OR compute from the fetched response
        // We'll compute from current `reviews` once it updates via another effect below.
      } catch (e) {
        console.error(e);
        if (!alive) return;
        setError("Failed to load teacher profile. Please try again.");
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [teacherId]);

  // Update title whenever reviews change
  useEffect(() => {
    setTeacherTitle(computeTitleFromReviews(reviews));
  }, [reviews]);

  const overallAverage = useMemo(() => {
    if (!teacherAverageRatings) return null;

    const nums = categories
      .map((c) => parseFloat(teacherAverageRatings[c.avgKey]))
      .filter((n) => !Number.isNaN(n));

    if (!nums.length) return null;
    const sum = nums.reduce((a, b) => a + b, 0);
    return (sum / nums.length).toFixed(1);
  }, [teacherAverageRatings, categories]);

  const scrollToRate = () =>
    rateRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  // Slider change
  const handleSliderChange = (category, value) => {
    setRatings((prev) => ({ ...prev, [category]: value }));
  };

  // Generated title from sliders (user input)
  const generatedTitle = useMemo(() => {
    const avg =
      (ratings.proficiency +
        ratings.connection +
        ratings.clarity +
        ratings.behavior +
        ratings.supportiveness) /
      5;
    return calculateTeacherTitle(avg);
  }, [ratings]);

  // Submit review (kept from your logic)
  const handleSubmit = async () => {
    const token = getToken();

    if (!token) {
      toast.warning("Please login or sign up first to submit a review.", {
        position: "top-right",
        autoClose: 2500,
      });

      setTimeout(() => {
        navigate("/signup");
        setTimeout(() => window.scrollTo(0, 0), 100);
      }, 1500);

      return;
    }

    if (!feedback.trim()) {
      toast.error("Please write a short feedback.", {
        position: "top-right",
        autoClose: 2500,
      });
      return;
    }

    const reviewData = {
      proficiency: ratings.proficiency,
      connection: ratings.connection,
      clarity: ratings.clarity,
      behavior: ratings.behavior,
      supportiveness: ratings.supportiveness,
      comment: feedback,
      is_anonymous: anonymous,
    };

    try {
      setSubmitting(true);

      await axios.post(
        `https://server.goatteachers.com/api/teachers/${teacherId}/reviews`,
        reviewData,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      toast.success("Review submitted!", {
        position: "top-right",
        autoClose: 2500,
      });

      setFeedback("");
      setAnonymous(false);

      await Promise.all([fetchTeacherAverage(), fetchReviews()]);
    } catch (e) {
      console.error(e);
      const msg =
        e?.response?.data?.message ||
        "Failed to submit review. Please try again.";

      toast.error(msg, {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Like/Dislike/Flag (kept)
  const handleLikeDislike = async (reviewId, action) => {
    const token = getToken();
    if (!token) {
      toast.warning("You must be logged in to do this.", {
        position: "top-right",
        autoClose: 2500,
      });
      return;
    }

    try {
      await axios.post(
        `https://server.goatteachers.com/api/teachers/${teacherId}/reviews/${reviewId}/like`,
        { action },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await fetchReviews();
    } catch (e) {
      console.error(e);
      toast.error("Failed to update reaction.", {
        position: "top-right",
        autoClose: 2500,
      });
    }
  };

  const handleFlag = async (reviewId) => {
    const reason = prompt("Please provide a reason for flagging the review:");
    if (!reason?.trim()) {
      toast.error("Flagging reason is required.", {
        position: "top-right",
        autoClose: 2500,
      });
      return;
    }

    const token = getToken();
    if (!token) {
      toast.warning("You must be logged in to flag a review.", {
        position: "top-right",
        autoClose: 2500,
      });
      return;
    }

    try {
      await axios.post(
        `https://server.goatteachers.com/api/teachers/${teacherId}/reviews/${reviewId}/flag`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Review flagged successfully", {
        position: "top-right",
        autoClose: 2500,
      });
      await fetchReviews();
    } catch (e) {
      console.error(e);
      toast.error("Failed to flag the review.", {
        position: "top-right",
        autoClose: 2500,
      });
    }
  };

  // Emoji mapping (kept)
  const emojiRating = {
    proficiency: ["🥀", "🌹", "⚡", "🌟", "🔥"],
    connection: ["🥀", "🌹", "⚡", "🌟", "🔥"],
    clarity: ["🥀", "🌹", "⚡", "🌟", "🔥"],
    behavior: ["🥀", "🌹", "⚡", "🌟", "🔥"],
    supportiveness: ["🥀", "🌹", "⚡", "🌟", "🔥"],
  };

  if (loading) {
    return (
      <div className="tp-page">
        <ToastContainer position="top-right" autoClose={3000} />
        <Container className="tp-shell">
          <div className="tp-skeleton tp-skeleton-hero" />
          <Row className="g-3">
            <Col lg={4}>
              <div className="tp-skeleton tp-skeleton-card" />
            </Col>
            <Col lg={8}>
              <div className="tp-skeleton tp-skeleton-card" />
            </Col>
          </Row>
          <div className="tp-skeleton tp-skeleton-big" />
        </Container>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="tp-page">
        <ToastContainer position="top-right" autoClose={3000} />
        <Container className="tp-shell">
          <div className="tp-error">
            <div className="tp-error-title">Something went wrong</div>
            <div className="tp-error-sub">{error}</div>
            <Button variant="dark" onClick={() => window.location.reload()}>
              Reload
            </Button>
          </div>
        </Container>
        <Footer />
      </div>
    );
  }

  if (!teacher) {
    return (
      <div className="tp-page">
        <ToastContainer position="top-right" autoClose={3000} />
        <Container className="tp-shell">
          <div className="tp-empty">Teacher not found.</div>
        </Container>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="tp-page">
        <Container className="tp-shell">
          {/* HERO */}
          <div className="tp-hero">
            <div className="tp-hero-left">
              <div className="tp-title">{teacher.teacher_name}</div>

              <div className="tp-meta">
                <div className="tp-meta-row">
                  <span className="tp-meta-label">Designation:</span>
                  <span className="tp-meta-value">
                    {teacher.designation || "Not available"}
                  </span>
                </div>
                <div className="tp-meta-row">
                  <span className="tp-meta-label">Department:</span>
                  <span className="tp-meta-value">
                    {teacher.department || "Not available"}
                  </span>
                </div>
                <div className="tp-meta-row">
                  <span className="tp-meta-label">Institute:</span>
                  <span className="tp-meta-value">
                    {teacher?.Institute?.institute_name || "Not Available"}
                  </span>
                </div>
              </div>
            </div>

            <div className="tp-hero-actions">
              <button className="tp-btn tp-btn-primary" onClick={scrollToRate}>
                Rate this Faculty
              </button>
            </div>
          </div>

          {/* OVERVIEW */}
          <Row className="g-3 mb-4">
            <Col lg={4}>
              <Card className="tp-card tp-card-center">
                {/* <div className="tp-card-title">Hero Title</div> */}
                <div className="tp-heroTitle">
                  {teacherTitle || "No title yet"}
                </div>

                <div className="tp-divider" />

                <div className="tp-card-title">Overall Rating</div>
                {overallAverage ? (
                  <div className="tp-score">
                    <span className="tp-score-value">{overallAverage}</span>
                    <span className="tp-score-den">/ 5</span>
                  </div>
                ) : (
                  <div className="tp-empty">Be the first to review ⭐</div>
                )}
                <div className="tp-muted">
                  {reviews.length} review{reviews.length === 1 ? "" : "s"}
                </div>
              </Card>
            </Col>

            <Col lg={8}>
              <Card className="tp-card">
                <div className="tp-card-head">
                  <div className="tp-card-title">Category Ratings</div>
                  <div className="tp-pill">
                    {reviews.length} review{reviews.length === 1 ? "" : "s"}
                  </div>
                </div>

                <div className="tp-breakdown">
                  {categories.map((c) => {
                    const rating = teacherAverageRatings
                      ? parseFloat(teacherAverageRatings[c.avgKey])
                      : 0;
                    const width = Math.max(
                      0,
                      Math.min(100, (rating / 5) * 100),
                    );

                    return (
                      <div className="tp-break-row" key={c.avgKey}>
                        <div className="tp-break-left">
                          <div className="tp-break-icon">{c.icon}</div>
                          <div className="tp-break-label">{c.label}</div>
                        </div>

                        <div className="tp-break-right">
                          <div className="tp-bar">
                            <div
                              className="tp-bar-fill"
                              style={{
                                width: `${width}%`,
                                backgroundColor: getRatingColor(rating),
                              }}
                            />
                          </div>
                          <div className="tp-break-num">
                            {rating ? rating.toFixed(1) : "—"}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </Col>
          </Row>

          {/* RATE SECTION */}
          <div ref={rateRef} className="tp-section">
            <div className="tp-section-title">Add Your Rating</div>
            <div className="tp-section-sub">
              Be honest. Be kind. Be respectful.
            </div>

            <Card className="tp-card tp-rate-card">
              <Row className="g-3">
                {categories.map((c) => {
                  const v = ratings[c.key];

                  return (
                    <Col md={6} key={c.key}>
                      <div className="tp-factor">
                        <div className="tp-factor-top">
                          <div className="tp-factor-title">{c.label}</div>

                          <div
                            className="tp-factor-pill"
                            style={{
                              borderColor: getRatingColor(v),
                              color: getRatingColor(v),
                            }}
                          >
                            {
                              [
                                "Poor",
                                "Fair",
                                "Good",
                                "Very Good",
                                "Excellent",
                              ][v - 1]
                            }
                          </div>
                        </div>

                        <div className="tp-slider">
                          <div className="tp-slider-side tp-slider-nay">
                            Nay
                          </div>

                          <div className="tp-slider-track">
                            <input
                              className="tp-slider-input"
                              type="range"
                              min="1"
                              max="5"
                              step="1"
                              value={v}
                              onChange={(e) =>
                                handleSliderChange(
                                  c.key,
                                  parseInt(e.target.value),
                                )
                              }
                            />

                            {/*  emoji centered vertically on track (CSS handles top:50% + translateY(-50%)) */}
                            <div
                              className="tp-slider-emoji"
                              style={{ left: `${(v - 1) * 25}%` }}
                            >
                              {emojiRating[c.key]?.[v - 1] || "⚡"}
                            </div>
                          </div>

                          <div className="tp-slider-side tp-slider-yay">
                            Yay
                          </div>
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Row>

              {/* Generated Title */}
              <div className="tp-generated">
                <div className="tp-generated-left">
                  <div className="tp-generated-title">Your Generated Title</div>
                  <div className="tp-muted">Based on your sliders</div>
                </div>
                <div className="tp-generated-right">
                  <span className="tp-generated-badge">{generatedTitle}</span>
                </div>
              </div>

              {/* Feedback box + actions */}
              <div className="tp-reviewbox">
                <div className="tp-reviewbox-title">Leave Your Feedback</div>

                <Form.Control
                  as="textarea"
                  rows={4}
                  className="tp-textarea"
                  placeholder="এই সুযোগে বলো — কোন টিচার আসলে কিং, আর কে শুধু স্লাইড চালায়!"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                />

                <div className="tp-actions">
                  {/* Toggle */}
                  <div className="tp-toggle">
                    <label className="tp-switch">
                      <input
                        type="checkbox"
                        checked={anonymous}
                        onChange={() => setAnonymous((s) => !s)}
                      />
                      <span className="tp-switch-ui" />
                    </label>
                    <div className="tp-toggle-text">
                      {anonymous ? "Anonymity ON" : "Anonymity OFF"}
                    </div>
                  </div>

                  {/* Submit */}
                  <button
                    className="tp-btn tp-btn-primary"
                    onClick={handleSubmit}
                    disabled={submitting && isLoggedIn}
                  >
                    {!isLoggedIn
                      ? "Sign Up to Submit Review"
                      : submitting
                        ? "Submitting..."
                        : "Submit Rating"}
                  </button>
                </div>
              </div>
            </Card>
          </div>

          {/* REVIEWS */}
          <div className="tp-section">
            <div className="tp-section-head">
              <div className="tp-section-title">
                User Ratings{" "}
                <span className="tp-count">({reviews.length})</span>
              </div>
            </div>

            {reviews.length ? (
              <Row className="g-3">
                {reviews.map((review) => {
                  const avg =
                    (review.proficiency_rating +
                      review.connection_rating +
                      review.clarity_rating +
                      review.behavior_rating +
                      review.supportiveness_rating) /
                    5;

                  const avgFixed = avg.toFixed(1);

                  return (
                    <Col lg={6} key={review.teacher_rating_id}>
                      <Card className="tp-card tp-review-card">
                        {/* Header */}
                        <div className="tp-review-head">
                          <div className="tp-review-author">
                            {review.is_anonymous
                              ? "Anonymous"
                              : review?.User?.name || "User"}
                          </div>

                          <div
                            className="tp-review-score"
                            style={{
                              borderColor: getRatingColor(avg),
                              color: getRatingColor(avg),
                              fontWeight: 800,
                              letterSpacing: "0.3px",
                            }}
                          >
                            {calculateTeacherTitle(avg)}
                          </div>
                        </div>

                        {/* Comment */}
                        <div className="tp-review-text">{review.comment}</div>

                        {/* Breakdown */}
                        <div className="tp-review-breakdown">
                          {[
                            {
                              label: "Proficiency",
                              val: review.proficiency_rating,
                            },
                            {
                              label: "Connection",
                              val: review.connection_rating,
                            },
                            { label: "Clarity", val: review.clarity_rating },
                            { label: "Behavior", val: review.behavior_rating },
                            {
                              label: "Supportiveness",
                              val: review.supportiveness_rating,
                            },
                          ].map((x) => {
                            const width = Math.max(
                              0,
                              Math.min(100, (x.val / 5) * 100),
                            );

                            return (
                              <div className="tp-mini" key={x.label}>
                                <div className="tp-mini-top">
                                  <div className="tp-mini-label">{x.label}</div>
                                  <div className="tp-mini-num">{x.val}/5</div>
                                </div>

                                <div className="tp-mini-bar">
                                  <div
                                    className="tp-mini-fill"
                                    style={{
                                      width: `${width}%`,
                                      backgroundColor: getRatingColor(x.val),
                                    }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Actions */}
                        <div className="tp-review-actions">
                          <button
                            className="tp-chip"
                            onClick={() =>
                              handleLikeDislike(
                                review.teacher_rating_id,
                                "like",
                              )
                            }
                          >
                            👍 {review.likes}
                          </button>

                          <button
                            className="tp-chip"
                            onClick={() =>
                              handleLikeDislike(
                                review.teacher_rating_id,
                                "dislike",
                              )
                            }
                          >
                            👎 {review.dislikes}
                          </button>

                          <button
                            className="tp-chip tp-chip-warn"
                            onClick={() => handleFlag(review.teacher_rating_id)}
                          >
                            🚩 Flag
                          </button>
                        </div>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            ) : (
              <Card className="tp-card tp-empty-card">
                <div className="tp-empty">
                  No reviews yet. Be the first to review ⭐
                </div>
                <button
                  className="tp-btn tp-btn-primary"
                  onClick={scrollToRate}
                >
                  Write a review
                </button>
              </Card>
            )}
          </div>
        </Container>
      </div>

      <Footer />
    </>
  );
}

export default TeacherPage;
